import React, { useState } from "react";
import { Box, Button, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import AgentBaseCostBadge from "../../../common/AgentBaseCostBadge";
import AgentConfigSection from "../AgentConfigSection";
import { BASE_COST_CONFIG } from "../../../../utils/agentBaseCost";
import { getModelCatalogEntry } from "../../../../utils/modelCatalog";
import { isSelfConfigurationEditEnabled } from "../selfConfiguration";

const SECTION_CARD_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px",
  p: 2,
  backgroundColor: "action.hover",
};

/**
 * Ordered breakdown metadata. `weight` references a BASE_COST_CONFIG per-item
 * token cost so the tab can recover the underlying item count (tokens are an
 * exact multiple of the weight). `description` explains what the cost is;
 * `tip` is optimization guidance for the reduce-cost section only. Items with
 * `fixed` cannot be reduced through configuration.
 */
const COST_ITEM_META = [
  {
    key: "systemPrompt",
    label: "System prompt",
    description: "Instructions and persona text sent at the start of every turn.",
    tip: "Trim verbose or redundant instructions — every word ships on every turn.",
  },
  {
    key: "tools",
    label: "Tool definitions",
    weight: "perToolTokens",
    unit: "tool",
    description: "JSON schemas for each enabled tool, included on every turn.",
    tip: "Disable tools this agent does not use — each one injects its full schema every turn.",
  },
  {
    key: "toolInstructions",
    label: "Tool instructions",
    description: "Per-tool guidance you added for how the agent should use each tool.",
    tip: "Keep guidance concise or remove it where the tool is self-explanatory.",
  },
  {
    key: "knowledgeContext",
    label: "Manual knowledge context",
    description: "Reference text from Knowledge, injected in full on every turn.",
    tip: "Move large reference text into Knowledge files so only relevant chunks load (RAG).",
  },
  {
    key: "knowledgeFiles",
    label: "Knowledge files",
    weight: "perKnowledgeFileTokens",
    unit: "file",
    description: "Reference footprint of files attached in Knowledge.",
  },
  {
    key: "knowledgeTables",
    label: "Knowledge tables",
    weight: "perKnowledgeTableTokens",
    unit: "table",
    description: "Reference footprint of tables attached in Knowledge.",
  },
  {
    key: "configuredTables",
    label: "Scoped tables",
    weight: "perConfiguredTableTokens",
    unit: "table",
    description: "Schema descriptors for database tables scoped to this agent.",
    tip: "Scope only the tables the agent reads or writes.",
  },
  {
    key: "workflows",
    label: "Workflows",
    weight: "perWorkflowTokens",
    unit: "workflow",
    description: "Descriptors for workflows this agent can trigger.",
    tip: "Remove workflows the agent never triggers.",
  },
  {
    key: "configuredAgents",
    label: "Delegated agents",
    weight: "perAgentTokens",
    unit: "agent",
    description: "Descriptors for other agents this agent can delegate to.",
    tip: "Remove delegate agents this agent does not call.",
  },
  {
    key: "mcpTools",
    label: "MCP tools",
    weight: "perMcpToolTokens",
    unit: "MCP tool",
    description: "JSON schemas for allowlisted tools from connected MCP servers.",
    tip: "Narrow the per-server allowlist to only the remote tools this agent needs.",
  },
  {
    key: "baseline",
    label: "Base scaffolding",
    fixed: true,
    description: "Fixed framing and formatting the runtime adds every turn. Not configurable.",
  },
];

/** Stable segment colors keyed by breakdown item — same category, same color everywhere. */
function resolveCostItemColor(key, theme) {
  const mutedFixed = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "dark" ? 0.28 : 0.16
  );
  const colors = {
    systemPrompt: theme.palette.primary.main,
    tools: "#8b5cf6",
    toolInstructions: "#6366f1",
    knowledgeContext: theme.palette.info.main,
    knowledgeFiles: "#14b8a6",
    knowledgeTables: "#10b981",
    configuredTables: theme.palette.warning.main,
    workflows: "#f97316",
    configuredAgents: "#ec4899",
    mcpTools: "#3b82f6",
    baseline: mutedFixed,
  };
  return colors[key] ?? theme.palette.primary.main;
}

const formatTokens = (value) => Math.round(value || 0).toLocaleString();

const formatUnitCount = (count, unit) =>
  `${count.toLocaleString()} ${unit}${count === 1 ? "" : "s"}`;

function deriveItemCount(item, tokens) {
  if (!item.weight) return null;
  const weight = BASE_COST_CONFIG[item.weight];
  if (!weight) return null;
  return Math.round(tokens / weight);
}

function StackedCostBar({ items, rawTokens, hoveredKey, onHoverKey }) {
  const visibleItems = items.filter(({ tokens }) => tokens > 0);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Box sx={{ px: 0, pb: 2, pt: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.25,
        }}
      >
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "text.secondary" }}>
          Composition
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
          ~{formatTokens(rawTokens)} tokens
        </Typography>
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          gap: "3px",
          height: 32,
          p: "3px",
          borderRadius: "8px",
          bgcolor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.35 : 0.55),
          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.divider, 0.9)}`,
        })}
      >
        {visibleItems.map(({ item, tokens, color }, index) => {
          const share = rawTokens > 0 ? tokens / rawTokens : 0;
          const isFirst = index === 0;
          const isLast = index === visibleItems.length - 1;
          const isHighlighted = hoveredKey === item.key;
          const isDimmed = Boolean(hoveredKey) && !isHighlighted;

          return (
            <Tooltip
              key={item.key}
              title={`${item.label} · ~${formatTokens(tokens)} tokens (${Math.round(
                share * 100
              )}%)`}
              placement="top"
              arrow
            >
              <Box
                onMouseEnter={() => onHoverKey(item.key)}
                sx={{
                  flex: `${tokens} 1 0`,
                  minWidth: share >= 0.02 ? 6 : 3,
                  height: "100%",
                  borderRadius: isFirst && isLast ? "5px" : isFirst ? "5px 2px 2px 5px" : isLast ? "2px 5px 5px 2px" : "2px",
                  bgcolor: color,
                  cursor: "default",
                  opacity: isDimmed ? 0.3 : 1,
                  filter: isHighlighted ? "brightness(1.12)" : "none",
                  boxShadow: isHighlighted ? `0 0 0 2px ${alpha(color, 0.55)}` : "none",
                  transition: "opacity 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease",
                  zIndex: isHighlighted ? 1 : 0,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function CostBreakdownRow({ item, tokens, rawTokens, color, hoveredKey, onHoverKey }) {
  const share = rawTokens > 0 ? tokens / rawTokens : 0;
  const count = deriveItemCount(item, tokens);
  const isHighlighted = hoveredKey === item.key;
  const isDimmed = Boolean(hoveredKey) && !isHighlighted;

  return (
    <Box
      onMouseEnter={() => onHoverKey(item.key)}
      sx={{
        py: 1,
        mx: -1,
        px: 1,
        borderRadius: "6px",
        opacity: isDimmed ? 0.45 : 1,
        bgcolor: isHighlighted ? "action.selected" : "transparent",
        transition: "opacity 0.15s ease, background-color 0.15s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          mb: item.description ? 0.5 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 3,
              height: 14,
              borderRadius: "2px",
              bgcolor: color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}
          >
            {item.label}
            {count !== null ? (
              <Typography
                component="span"
                sx={{ fontSize: "12px", fontWeight: 400, color: "text.secondary", ml: 0.75 }}
              >
                · {formatUnitCount(count, item.unit)}
              </Typography>
            ) : null}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
          ~{formatTokens(tokens)} tokens · {Math.round(share * 100)}%
        </Typography>
      </Box>
      {item.description ? (
        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.5, pl: 2.625 }}
        >
          {item.description}
        </Typography>
      ) : null}
    </Box>
  );
}

function CostBreakdownChart({ items, rawTokens }) {
  const [hoveredKey, setHoveredKey] = useState(null);

  return (
    <Box onMouseLeave={() => setHoveredKey(null)}>
      <StackedCostBar
        items={items}
        rawTokens={rawTokens}
        hoveredKey={hoveredKey}
        onHoverKey={setHoveredKey}
      />
      {items.map(({ item, tokens, color }, index) => (
        <React.Fragment key={item.key}>
          {index > 0 ? (
            <Box sx={{ borderTop: 1, borderColor: "divider" }} />
          ) : null}
          <CostBreakdownRow
            item={item}
            tokens={tokens}
            rawTokens={rawTokens}
            color={color}
            hoveredKey={hoveredKey}
            onHoverKey={setHoveredKey}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

/**
 * Wraps a single optimization instruction in a self-config kickoff message the
 * agent can act on with its get_self_config / update_self_config tools. The
 * change is scoped to exactly the one tip so the agent does not touch unrelated
 * configuration.
 */
function buildCostReductionMessage(instruction) {
  return [
    "I want to reduce your operating cost.",
    "",
    "First, call get_self_config to inspect your current configuration. Then apply ONLY the following change using update_self_config (send just the fields you are changing), and briefly explain what you changed and why it lowers cost:",
    "",
    instruction,
    "",
    "Do not modify any unrelated configuration.",
  ].join("\n");
}

/**
 * Produces optimization tips as objects: `text` is shown to the user,
 * `instruction` is the concrete change sent to the agent (null when the tip is
 * not actionable), and `applicable` gates the apply affordance.
 */
function buildOptimizationTips(breakdown, modelCostTier, modelLabel) {
  const tips = [];

  if (modelCostTier >= 3) {
    tips.push({
      text: `This agent runs on a higher-cost model (${modelLabel}). For routine or high-volume work, a cheaper model lowers the price of every turn.`,
      instruction:
        "Switch to a more cost-efficient model that can still handle this agent's tasks well.",
      applicable: true,
    });
  }

  const reducible = COST_ITEM_META.filter(
    (item) => !item.fixed && (breakdown[item.key] || 0) > 0
  ).sort((a, b) => (breakdown[b.key] || 0) - (breakdown[a.key] || 0));

  reducible.slice(0, 3).forEach((item) => {
    if (!item.tip) return;
    const tokens = breakdown[item.key] || 0;
    const count = deriveItemCount(item, tokens);
    const detail =
      count !== null
        ? `currently ${formatUnitCount(count, item.unit)}, ~${formatTokens(
            tokens
          )} tokens`
        : `currently ~${formatTokens(tokens)} tokens`;
    tips.push({
      text: item.tip,
      instruction: `${item.tip} (${detail})`,
      applicable: true,
    });
  });

  if (tips.length === 0) {
    tips.push({
      text: "This agent is already lean — its standing context is close to the fixed minimum.",
      instruction: null,
      applicable: false,
    });
  }

  return tips;
}

export default function CostTab({
  agentBaseCost,
  model,
  selfConfigurationMode,
  agentId,
  onApplyTip,
}) {
  const theme = useTheme();
  const cost = agentBaseCost || { rawTokens: 0, tier: 1, modelCostTier: 1, breakdown: {} };
  const breakdown = cost.breakdown || {};
  const modelEntry = model ? getModelCatalogEntry(model) : null;
  const modelLabel = modelEntry?.label || model || "the selected model";
  const canApply =
    typeof onApplyTip === "function" &&
    Boolean(agentId) &&
    isSelfConfigurationEditEnabled(selfConfigurationMode);

  const orderedItems = COST_ITEM_META.map((item) => ({
    item,
    tokens: breakdown[item.key] || 0,
  }))
    .filter(({ item, tokens }) => tokens > 0 || item.key === "baseline")
    .sort((a, b) => {
      if (a.item.fixed && !b.item.fixed) return 1;
      if (!a.item.fixed && b.item.fixed) return -1;
      return b.tokens - a.tokens;
    });

  const itemsWithColors = orderedItems.map((entry) => ({
    ...entry,
    color: resolveCostItemColor(entry.item.key, theme),
  }));

  const tips = buildOptimizationTips(breakdown, cost.modelCostTier, modelLabel);

  return (
    <>
      <AgentConfigSection id="costSummary">
        <Box sx={SECTION_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}
              >
                Base operating cost
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}
              >
                Standing context sent on every turn, priced by {modelLabel}.
              </Typography>
            </Box>
            <AgentBaseCostBadge
              tier={cost.tier}
              rawTokens={cost.rawTokens}
              showLabel={false}
              sx={{ fontSize: "13px" }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              lineHeight: 1.6,
              mt: 1.5,
            }}
          >
            This is the cost the agent carries by how it is configured — its
            system prompt, tool definitions, knowledge, and scoped resources.
            It is charged on every turn before the agent does any work. It does
            not include the size of the user's request, the agent's output, or
            how many loop iterations a task takes — those vary per run.
          </Typography>
        </Box>
      </AgentConfigSection>

      <AgentConfigSection id="costBreakdown" sx={{ mt: 2.5 }}>
        <Typography className="org-field-label">Where the cost comes from</Typography>
        <Box sx={{ ...SECTION_CARD_SX, mt: 1, pt: 1.5, pb: 1 }}>
          <CostBreakdownChart items={itemsWithColors} rawTokens={cost.rawTokens} />
        </Box>
      </AgentConfigSection>

      <AgentConfigSection id="costOptimization" sx={{ mt: 2.5 }}>
        <Typography className="org-field-label">How to reduce it</Typography>
        <Box sx={{ ...SECTION_CARD_SX, mt: 1 }}>
          {tips.map((tip, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                py: 0.75,
              }}
            >
              <LightbulbOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary", mt: "1px", flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.5, flex: 1 }}
              >
                {tip.text}
              </Typography>
              {canApply && tip.applicable ? (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AutoFixHighOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() =>
                    onApplyTip(buildCostReductionMessage(tip.instruction))
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 500,
                    flexShrink: 0,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    py: 0,
                  }}
                >
                  Apply
                </Button>
              ) : null}
            </Box>
          ))}
          {typeof onApplyTip === "function" && !canApply ? (
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.disabled",
                lineHeight: 1.5,
                mt: 1,
              }}
            >
              {agentId
                ? "Turn on \u201CAllow agent to edit its own config\u201D in the Advanced tab to apply these in one click."
                : "Save this agent first — then it can apply these changes from its chat."}
            </Typography>
          ) : null}
        </Box>
      </AgentConfigSection>
    </>
  );
}
